import type { ISODate, Month } from '@contracts/common';

/**
 * A business rule the caller violated. Domain code is pure and knows nothing about HTTP; the API layer maps
 * `code` onto its own error envelope. Defined here because `dates.ts` is the leaf every other domain module
 * may import.
 */
export class DomainRuleError extends Error {
  constructor(readonly code: string, message: string, readonly field?: string) {
    super(`${code}: ${message}`);
    this.name = 'DomainRuleError';
  }
}

/** Fixed business timezone (spec §5.1). UTC+7 all year; the platform database carries the actual rules. */
export const BUSINESS_TIME_ZONE = 'Asia/Ho_Chi_Minh';

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH = /^\d{4}-\d{2}$/;
const MIN_YEAR = 1;
const MAX_YEAR = 9999;
const DAY_MS = 86_400_000;

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/** Days in a Gregorian month; February follows the full leap rule, including the 100/400 exceptions. */
export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

/**
 * Validates a calendar date written exactly as `YYYY-MM-DD` and returns it unchanged. The string is checked
 * against the Gregorian calendar itself — never handed to `new Date()`, which would turn 2026-09-31 into
 * 1 October and accept a trailing time. Years run 0001–9999.
 */
export function parseDate(value: string): ISODate {
  if (typeof value !== 'string' || !DATE.test(value)) {
    throw new DomainRuleError('INVALID_DATE', `ngày phải có dạng YYYY-MM-DD: ${String(value)}`);
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < MIN_YEAR || year > MAX_YEAR) throw new DomainRuleError('INVALID_DATE', `năm ngoài khoảng 0001–9999: ${value}`);
  if (month < 1 || month > 12) throw new DomainRuleError('INVALID_DATE', `tháng không hợp lệ: ${value}`);
  if (day < 1 || day > daysInMonth(year, month)) throw new DomainRuleError('INVALID_DATE', `ngày không tồn tại: ${value}`);
  return value;
}

/** Validates a business month written exactly as `YYYY-MM` (stored as the first day of that month). */
export function parseMonth(value: string): Month {
  if (typeof value !== 'string' || !MONTH.test(value)) {
    throw new DomainRuleError('INVALID_MONTH', `tháng phải có dạng YYYY-MM: ${String(value)}`);
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  if (year < MIN_YEAR || year > MAX_YEAR) throw new DomainRuleError('INVALID_MONTH', `năm ngoài khoảng 0001–9999: ${value}`);
  if (month < 1 || month > 12) throw new DomainRuleError('INVALID_MONTH', `tháng không hợp lệ: ${value}`);
  return value;
}

/** The first calendar day of a business month, as an ISO date. */
export function monthStart(month: Month): ISODate {
  return `${parseMonth(month)}-01`;
}

/** The business month a calendar date belongs to. */
export function monthOf(date: ISODate): Month {
  return parseDate(date).slice(0, 7);
}

/**
 * The days a work item's deadline may fall on (PO ruling 2026-09-25), both ends included: a year such as 0202 is a
 * slip of the keyboard, not a plan. The API refuses a new deadline outside it and the date pickers offer none; a
 * deadline already stored outside it is left alone until someone changes it.
 */
export const DEADLINE_MIN: ISODate = '2000-01-01';
export const DEADLINE_MAX: ISODate = '2099-12-31';
/** What the API and the web say about a deadline outside the window. */
export const DEADLINE_OUT_OF_RANGE = 'Hạn chót phải trong khoảng 01/01/2000 – 31/12/2099';

/** Whether a valid calendar date lies in the deadline window. `YYYY-MM-DD` strings compare in calendar order. */
export function inDeadlineWindow(date: ISODate): boolean {
  return date >= DEADLINE_MIN && date <= DEADLINE_MAX;
}

/**
 * The calendar day in the business timezone at instant `now`. The only conversion from instant to business
 * day in the system; API, worker and UI all read the day from here so nothing drifts by a timezone.
 */
export function businessDate(now: Date): ISODate {
  const ms = now.getTime();
  if (!Number.isFinite(ms)) throw new DomainRuleError('INVALID_DATE', 'thời điểm không hợp lệ');
  const parts = dayFormatter.formatToParts(now);
  const part = (type: 'year' | 'month' | 'day') => {
    const found = parts.find((p) => p.type === type);
    if (!found) throw new DomainRuleError('INVALID_DATE', `thiếu thành phần ${type} của ngày`);
    return found.value;
  };
  return `${pad(Number(part('year')), 4)}-${pad(Number(part('month')), 2)}-${pad(Number(part('day')), 2)}`;
}

/**
 * Calendar-day arithmetic on a validated date. The epoch instant is built with `setUTCFullYear` so years
 * 1–99 stay themselves instead of being mapped into the 1900s, and the result must remain inside 0001–9999.
 */
export function addDays(date: ISODate, days: number): ISODate {
  const valid = parseDate(date);
  if (!Number.isSafeInteger(days)) throw new DomainRuleError('INVALID_DATE', `số ngày phải là số nguyên: ${String(days)}`);
  const at = new Date(0);
  at.setUTCFullYear(Number(valid.slice(0, 4)), Number(valid.slice(5, 7)) - 1, Number(valid.slice(8, 10)));
  at.setUTCHours(0, 0, 0, 0);
  const moved = new Date(at.getTime() + days * DAY_MS);
  const year = moved.getUTCFullYear();
  if (!Number.isFinite(moved.getTime()) || year < MIN_YEAR || year > MAX_YEAR) {
    throw new DomainRuleError('INVALID_DATE', `ngày kết quả ngoài khoảng 0001–9999: ${valid} + ${days}`);
  }
  return `${pad(year, 4)}-${pad(moved.getUTCMonth() + 1, 2)}-${pad(moved.getUTCDate(), 2)}`;
}

export type QuarterNumber = 1 | 2 | 3 | 4;
export type Quarter = { year: number; quarter: QuarterNumber };

/** The quarter of the business day at instant `now` — the calendar year and quarter, not the UTC one. */
export function currentQuarter(now: Date): Quarter {
  const day = businessDate(now);
  return { year: Number(day.slice(0, 4)), quarter: quarterOfMonth(Number(day.slice(5, 7))) };
}

/** The quarter a calendar month number (1–12) belongs to. */
export function quarterOfMonth(month: number): QuarterNumber {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new DomainRuleError('INVALID_MONTH', `tháng không hợp lệ: ${String(month)}`);
  }
  return (Math.ceil(month / 3) as QuarterNumber);
}

/**
 * The fixed offset of the business timezone. Asia/Ho_Chi_Minh has kept UTC+7 all year since 1975, so a local
 * business time converts to an instant without a timezone database (S3 L2 workdays and reminders).
 */
export const BUSINESS_UTC_OFFSET = '+07:00';

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Days since 1970-01-01 of a validated calendar date (negative before it). */
function epochDay(date: ISODate): number {
  const valid = parseDate(date);
  const at = new Date(0);
  at.setUTCFullYear(Number(valid.slice(0, 4)), Number(valid.slice(5, 7)) - 1, Number(valid.slice(8, 10)));
  return Math.round(at.getTime() / DAY_MS);
}

/** Calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: ISODate, to: ISODate): number {
  return epochDay(to) - epochDay(from);
}

/** The ISO weekday of a calendar date: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: ISODate): number {
  const day = ((epochDay(date) % 7) + 7) % 7; // 1970-01-01 was a Thursday (ISO 4)
  return ((day + 3) % 7) + 1;
}

/** S3 L2 D46: a workday is Monday to Friday of the business calendar; there are no holidays (§1.1 ★). */
export function isWorkday(date: ISODate): boolean {
  return isoWeekday(date) <= 5;
}

/**
 * `n` workdays after `date` (before it when `n` is negative); `n = 0` returns `date` itself. A weekend start counts
 * from the next workday: Saturday + 1 is Monday. Every rule `days` parameter and `rescheduleOverdue` count this way.
 */
export function addWorkdays(date: ISODate, n: number): ISODate {
  const valid = parseDate(date);
  if (!Number.isSafeInteger(n)) throw new DomainRuleError('INVALID_DATE', `số ngày làm việc phải là số nguyên: ${String(n)}`);
  if (n === 0) return valid;
  const step = n > 0 ? 1 : -1;
  let weeks = Math.trunc(Math.abs(n) / 5);
  let rest = Math.abs(n) % 5;
  // Whole weeks jump 7 days and keep the weekday; the last week is always walked so a weekend start moves off it.
  if (rest === 0) { weeks -= 1; rest = 5; }
  let at = addDays(valid, step * weeks * 7);
  while (rest > 0) {
    at = addDays(at, step);
    if (isWorkday(at)) rest -= 1;
  }
  return at;
}

/**
 * The workdays after `from` up to and including `to` — so `workdaysBetween(d, addWorkdays(d, n)) === n` for a
 * workday `d`. Negative when `to` is earlier than `from`.
 */
export function workdaysBetween(from: ISODate, to: ISODate): number {
  const span = daysBetween(from, to);
  if (span < 0) return -workdaysBetween(to, from);
  let count = Math.trunc(span / 7) * 5;
  let at = addDays(from, Math.trunc(span / 7) * 7);
  for (let left = span % 7; left > 0; left--) {
    at = addDays(at, 1);
    if (isWorkday(at)) count += 1;
  }
  return count;
}

/** The instant of local business time `hhmm` ('HH:MM') on `date`. */
export function businessInstant(date: ISODate, hhmm: string): Date {
  const valid = parseDate(date);
  if (typeof hhmm !== 'string' || !HHMM.test(hhmm)) {
    throw new DomainRuleError('INVALID_TIME', `giờ phải có dạng HH:MM: ${String(hhmm)}`);
  }
  const at = new Date(`${valid}T${hhmm}:00${BUSINESS_UTC_OFFSET}`);
  if (!Number.isFinite(at.getTime())) throw new DomainRuleError('INVALID_TIME', `thời điểm không hợp lệ: ${valid} ${hhmm}`);
  return at;
}

/**
 * `hhmm` on the first workday after the business day of `now` — never today, even before that hour. Friday,
 * Saturday and Sunday all give Monday. The snooze "Sáng mai" is `nextWorkdayAt(now, '07:30')` (§7.3).
 */
export function nextWorkdayAt(now: Date, hhmm: string): Date {
  return businessInstant(addWorkdays(businessDate(now), 1), hhmm);
}

const ISO_WEEK = /^(\d{4})-W(\d{2})$/;

/** The Monday of ISO week 1 of `year`: the week holding January 4th. */
function isoWeekOneMonday(year: number): ISODate {
  const jan4 = `${pad(year, 4)}-01-04`;
  return addDays(jan4, 1 - isoWeekday(jan4));
}

/**
 * The ISO week of a calendar date, `YYYY-Www`, with the ISO year: the week belongs to the year of its Thursday, so
 * 2027-01-01 (a Friday) is `2026-W53` and 2019-12-30 is `2020-W01` (S3 L3 D117, the weekly briefing's period).
 */
export function isoWeekOf(date: ISODate): string {
  const thursday = addDays(date, 4 - isoWeekday(date));
  const year = Number(thursday.slice(0, 4));
  const week = Math.floor(daysBetween(isoWeekOneMonday(year), thursday) / 7) + 1;
  return `${pad(year, 4)}-W${pad(week, 2)}`;
}

/** The Monday and Sunday of an ISO week `YYYY-Www`; a week the ISO year does not have is an error. */
export function isoWeekRange(week: string): { monday: ISODate; sunday: ISODate } {
  const match = typeof week === 'string' ? ISO_WEEK.exec(week) : null;
  if (match === null) throw new DomainRuleError('INVALID_WEEK', `tuần phải có dạng YYYY-Www: ${String(week)}`);
  const monday = addDays(isoWeekOneMonday(Number(match[1])), (Number(match[2]) - 1) * 7);
  if (Number(match[2]) < 1 || isoWeekOf(monday) !== week) {
    throw new DomainRuleError('INVALID_WEEK', `năm ISO không có tuần này: ${week}`);
  }
  return { monday, sunday: addDays(monday, 6) };
}
